import React, { useCallback, useEffect, useState } from 'react';
import { formatDateTime } from '../lib/utils';
import api from '../services/api';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CheckCircle,
  WarningCircle,
  XCircle,
  Clock,
  ArrowsClockwise,
  Certificate,
  Lightbulb,
  Spinner,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const COLOR_TONES = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', accent: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', accent: 'bg-amber-500' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', accent: 'bg-red-500' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', accent: 'bg-slate-400' },
};

const STATUS_ICON = {
  Pass: CheckCircle,
  Borderline: WarningCircle,
  Fail: XCircle,
  Awaiting: Clock,
};

const PRIORITY_TONE = {
  blocker: { color: 'red', icon: XCircle, label: 'Blocker' },
  next: { color: 'amber', icon: Clock, label: 'Next' },
  investigate: { color: 'amber', icon: WarningCircle, label: 'Investigate' },
  volume: { color: 'amber', icon: Clock, label: 'Volume' },
  great: { color: 'emerald', icon: CheckCircle, label: 'On track' },
};

const PhaseCard = ({ phase }) => {
  const tone = COLOR_TONES[phase.grade.color] || COLOR_TONES.slate;
  const Icon = STATUS_ICON[phase.grade.label] || Clock;
  return (
    <Card
      className={`p-4 border ${tone.border} ${tone.bg}`}
      data-testid={`phase-card-${phase.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{phase.name}</p>
          <p className={`text-2xl font-bold mt-1 tabular-nums ${tone.text}`}>
            {phase.grade.pct}%
          </p>
          <p className="text-xs text-slate-500 mt-1">Target: {phase.target}</p>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${tone.bg} ${tone.text} border ${tone.border}`}>
          <Icon size={14} weight="fill" />
          <span className="text-xs font-semibold">{phase.grade.label}</span>
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-xs text-slate-600">{phase.rule}</p>
        <p className="text-xs text-slate-500 font-mono">{phase.metric}</p>
      </div>
    </Card>
  );
};

const FunnelBar = ({ stages }) => {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Lifecycle funnel</h3>
      <div className="space-y-1.5">
        {stages.map((s, i) => {
          const pct = (s.count / max) * 100;
          const isAccepted = s.stage.toLowerCase().includes('accepted') || s.stage.toLowerCase().includes('paid');
          return (
            <div key={s.stage} className="flex items-center gap-3" data-testid={`funnel-${i}`}>
              <span className="text-xs w-32 text-slate-600 shrink-0">{s.stage}</span>
              <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden">
                <div
                  className={`h-full ${isAccepted ? 'bg-emerald-400' : 'bg-indigo-300'} transition-all`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs font-mono tabular-nums w-12 text-right text-slate-700">
                {s.count}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const SubmissionsByDay = ({ rows }) => {
  if (!rows.length) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Submissions by day</h3>
        <p className="text-xs text-slate-500">No submissions yet.</p>
      </Card>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Submissions by day (last 14)</h3>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.day} className="flex items-center gap-2 text-xs" data-testid={`day-${r.day}`}>
            <span className="w-24 text-slate-500 font-mono">{r.day}</span>
            <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
              <div className="h-full bg-indigo-400" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <span className="font-mono tabular-nums w-8 text-right text-slate-700">{r.count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Recommendations = ({ items }) => (
  <Card className="p-4" data-testid="recommendations">
    <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
      <Lightbulb size={16} weight="duotone" className="text-amber-500" />
      Next steps
    </h3>
    <div className="space-y-2.5">
      {items.map((r, i) => {
        const tone = PRIORITY_TONE[r.priority] || PRIORITY_TONE.next;
        const Icon = tone.icon;
        const colors = COLOR_TONES[tone.color];
        return (
          <div
            key={`${r.priority}-${r.title}`}
            className={`p-3 rounded-md border ${colors.border} ${colors.bg} flex gap-2.5 items-start`}
            data-testid={`rec-${i}`}
          >
            <Icon size={16} weight="fill" className={`shrink-0 mt-0.5 ${colors.text}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${colors.text} mb-0.5`}>
                {tone.label}: {r.title}
              </p>
              <p className="text-xs text-slate-600 leading-relaxed">{r.detail}</p>
            </div>
          </div>
        );
      })}
    </div>
  </Card>
);

export default function OdmCertDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/odm-cert/status');
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load cert status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  // Auto-refresh every 60s while page is open
  useEffect(() => {
    const id = setInterval(fetchStatus, 60000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  return (
    <div className="space-y-6" data-testid="odm-cert-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Certificate size={32} className="text-indigo-700" weight="duotone" />
            ODM Certification Status
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Live grading against Ohio Administrative Code 5160-1-20 + ODM Companion Guide v19.0 thresholds.
            Auto-refreshes every 60 seconds.
          </p>
        </div>
        <Button
          onClick={fetchStatus}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="cert-refresh"
        >
          {loading ? <Spinner size={14} className="animate-spin mr-2" /> : <ArrowsClockwise size={14} className="mr-2" />}
          Refresh
        </Button>
      </header>

      {/* Quick action: contact ODM EDI Helpdesk to unblock CERT */}
      <Card className="p-4 bg-indigo-50 border-indigo-200 flex items-center justify-between gap-3" data-testid="cert-contact-odm-card">
        <div className="flex items-center gap-3">
          <EnvelopeSimple size={28} className="text-indigo-700" weight="duotone" />
          <div>
            <h3 className="text-sm font-semibold text-indigo-900">Need to contact ODM about CERT?</h3>
            <p className="text-xs text-indigo-800 mt-0.5">
              Pre-filled draft for the EDI Helpdesk requesting CERT-environment NPI registration + test recipient Medicaid IDs.
            </p>
          </div>
        </div>
        <Button asChild className="bg-indigo-600 hover:bg-indigo-700 shrink-0">
          <Link to="/admin/odm-cert/support-email" data-testid="open-cert-email-composer">
            <EnvelopeSimple size={14} className="mr-2" />
            Compose email
          </Link>
        </Button>
      </Card>

      {loading && !data && (
        <div className="flex justify-center py-16" data-testid="cert-loading">
          <Spinner size={32} className="animate-spin text-indigo-600" />
        </div>
      )}

      {data && (
        <>
          {/* Phase grades grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cert-phases">
            {data.phases.map((p) => <PhaseCard key={p.id} phase={p} />)}
          </div>

          {/* Funnel + per-day submissions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FunnelBar stages={data.funnel} />
            <SubmissionsByDay rows={data.submissions_by_day} />
          </div>

          {/* Recommendations */}
          <Recommendations items={data.recommendations} />

          {/* Summary footer */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
            <Badge variant="outline">{data.summary.total_submissions} submissions</Badge>
            <Badge variant="outline">{data.summary.ta1_accepted} TA1 accepted</Badge>
            <Badge variant="outline">{data.summary.ta1_rejected} TA1 rejected</Badge>
            <Badge variant="outline">{data.summary.ta1_pending} TA1 pending</Badge>
            <Badge variant="outline">{data.summary.real_999_count} real 999s</Badge>
            <Badge variant="outline">{data.summary.claims_with_277} claims w/ 277CA</Badge>
            <Badge variant="outline">{data.summary.claims_with_835} claims w/ 835</Badge>
            <Badge variant="outline">{data.summary.denial_pct}% denial rate</Badge>
            <span className="ml-auto">As of {formatDateTime(data.as_of)}</span>
          </div>
        </>
      )}
    </div>
  );
}
